import React, { useMemo } from 'react';
import { Button, LabeledSection, useToast } from '@app/core';
import { CenteredModal, InformationBox, TextArea } from 'components';
import {
  ParticipationStatus,
  useCloseSession,
  useCreateEvaluationSession,
  useIsAdmin,
  useInfiniteSessionParticipations,
  useSessionParticipationsCount,
} from '@app/api';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import * as Yup from 'yup';
import { ToggleGroup } from 'components/ToggleGroup';

const useStyles = createUseStyles((theme: Theme) => ({
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    gap: theme.marginBase * 2,
    marginTop: theme.marginBase * 2,
  },
  infoContainer: {
    marginBottom: theme.marginBase * 2,
  },
  hide: {
    display: 'none',
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose: () => any;
  sessionId: string;
}

type Values = {
  point: number;
  value: string | undefined;
  message: string | undefined;
};

const initialValues: Values = {
  point: -1,
  value: undefined,
  message: undefined,
};

enum ScoreName {
  VERY_UNSATISFIED = 'VERY_UNSATISFIED',
  UNSATISFIED = 'UNSATISFIED',
  SATISFIED = 'SATISFIED',
  VERY_SATISFIED = 'VERY_SATISFIED',
}

const pointValue = {
  [ScoreName.VERY_UNSATISFIED]: 0,
  [ScoreName.UNSATISFIED]: 33,
  [ScoreName.SATISFIED]: 66,
  [ScoreName.VERY_SATISFIED]: 100,
} as { [key: string]: number };

const validationSchema = Yup.object().shape({
  point: Yup.number().required().min(0).max(100),
  value: Yup.string().required(),
});

export const CloseSessionModal = ({
  sessionId,
  isOpen,
  onRequestClose,
}: Props) => {
  const toast = useToast();
  const { t } = useTranslation();
  const classes = useStyles();

  const { mutate: closeSession } = useCloseSession(sessionId, {
    onSuccess: onRequestClose,
    onError: toast.exception,
  });

  const { mutateAsync: createEvaluationSession } =
    useCreateEvaluationSession(sessionId);

  const { data: activeParticipationsCount } = useSessionParticipationsCount(
    sessionId,
    [ParticipationStatus.BOOKED, ParticipationStatus.PARTICIPATED],
  );
  const hasActiveParticipants = !!activeParticipationsCount;

  const isAdmin = useIsAdmin();

  const submit = async (values: Values) => {
    if (!isAdmin) {
      await createEvaluationSession({
        score: values.point,
        message: values.message,
      });
    }
    closeSession();
  };

  const options = useMemo(() => {
    return Object.values(ScoreName).map((name) => {
      return {
        value: name,
        label: t(`editSession.evaluation.score.${name}`),
      };
    });
  }, [t]);

  if (isAdmin) {
    return (
      <CenteredModal
        isOpen={isOpen}
        title={
          hasActiveParticipants
            ? t('sessions.closeSessionModal.unableToClose.title')
            : 'Close study'
        }
        buttons={[
          hasActiveParticipants && (
            <Button
              type="button"
              onClick={onRequestClose}
              small
              color="inkSecondary"
              variant="muted"
              text={t('sessions.closeSessionModal.unableToClose.cancel')}
            />
          ),
          !hasActiveParticipants && (
            <Button
              small
              color="red"
              text={t('sessions.closeSessionModal.close.closeAdmin')}
              onClick={() => {
                closeSession();
              }}
            />
          ),
        ]}
        onRequestClose={onRequestClose}
      >
        <InformationBox variant="danger" className={classes.infoContainer}>
          {hasActiveParticipants
            ? t('sessions.closeSessionModal.unableToClose.content')
            : t('sessions.closeSessionModal.close.content')}
        </InformationBox>
      </CenteredModal>
    );
  }

  return (
    <CenteredModal
      isOpen={isOpen}
      title={
        hasActiveParticipants
          ? t('sessions.closeSessionModal.unableToClose.title')
          : t('editSession.evaluation.title')
      }
    >
      {hasActiveParticipants && (
        <InformationBox variant="danger" className={classes.infoContainer}>
          {t('sessions.closeSessionModal.unableToClose.content')}
        </InformationBox>
      )}

      <Formik
        initialValues={initialValues}
        onSubmit={submit}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue }) => (
          <Form>
            {!hasActiveParticipants && (
              <>
                <LabeledSection
                  label={t('editSession.evaluation.labelScore')}
                  color="ink"
                >
                  <ToggleGroup
                    options={options}
                    selectedValue={values.value}
                    onChange={(name) => {
                      if (values.value === name) {
                        setFieldValue('value', undefined, false);
                        setFieldValue('point', -1, false);
                      } else {
                        setFieldValue('value', name, false);
                        setFieldValue('point', pointValue[name], false);
                      }
                    }}
                  />
                </LabeledSection>
                {values.value !== undefined && (
                  <>
                    <TextArea
                      name="message"
                      label={t('editSession.evaluation.labelComment')}
                    />
                    <InformationBox
                      variant="danger"
                      className={classes.infoContainer}
                    >
                      {t('sessions.closeSessionModal.close.content')}
                    </InformationBox>
                  </>
                )}
              </>
            )}
            <div className={classes.buttonContainer}>
              <Button
                onClick={onRequestClose}
                small
                color="inkSecondary"
                variant="muted"
                text={
                  hasActiveParticipants
                    ? t('sessions.closeSessionModal.unableToClose.cancel')
                    : t('sessions.closeSessionModal.close.cancel')
                }
              />
              {!hasActiveParticipants && values.value !== undefined ? (
                <Button
                  type="submit"
                  small
                  color="red"
                  text={t('editSession.evaluation.submit')}
                />
              ) : null}
            </div>
          </Form>
        )}
      </Formik>
    </CenteredModal>
  );
};
